import * as React from 'react';
import {useRef, useEffect, useState, useContext} from 'react';
import {
  provider,
  useIsomorphicLayoutEffect,
  animate,
  AnimatePresence,
  view,
  createView,
} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {BaseDrawer, DrawerContext} from './BaseDrawer';
import {CloseConfirmationDialog} from '../CloseConfirmationDialog';
import {useResizeObserver} from '../hooks/useResizeObserver';
import type {DrawerProps} from './DrawerProps';

type DrawerStackContext = {
  register(): void;
  setChildDrawerWidth(width: number): void;
  unregister(): void;
};

const ANIMATION_DURATION_SHORT = 100;
const ANIMATION_DURATION_MEDIUM = 150;
const ANIMATION_DURATION_LONG = 300;
const ANIMATION_EASING = 'cubic-bezier(0, .09, .4, 1)';

const DrawerStackContext = React.createContext<DrawerStackContext | undefined>(
  undefined,
);

function useDrawerStack(drawerRef: React.RefObject<HTMLElement>) {
  const parentContext = useContext(DrawerStackContext);
  const [isStackParent, setIsStackParent] = useState(false);
  const [childDrawerWidth, setChildDrawerWidth] = useState<number>();
  const timeline = animate.useCurrentTimeline();

  const {current: context} = useRef<DrawerStackContext>({
    register() {
      setIsStackParent(true);
    },
    setChildDrawerWidth(width) {
      setChildDrawerWidth(width);
      parentContext?.setChildDrawerWidth(width);
    },
    unregister() {
      drawerRef.current?.addEventListener(
        'transitionstart',
        () => setChildDrawerWidth(undefined),
        {once: true},
      );

      setIsStackParent(false);
    },
  });

  // if there is a parent context, we know we're a stacked child drawer
  // so we should register with our parent drawer
  useIsomorphicLayoutEffect(() => {
    if (timeline && parentContext) {
      let unregistered = false;
      const unlisten = timeline.listen({
        in: () => parentContext.register(),
        out: () => {
          parentContext.unregister();
          unregistered = true;
        },
      });

      return () => {
        unlisten();
        if (!unregistered) {
          parentContext.unregister();
        }
      };
    }
  }, [parentContext, timeline]);

  // if a child registered its drawer with us, set its width
  // as a custom property so we can animate ourselves accordingly
  useIsomorphicLayoutEffect(() => {
    const drawer = drawerRef.current;
    if (drawer && childDrawerWidth) {
      drawer.style.setProperty('--child-drawer-width', `${childDrawerWidth}px`);

      return () => {
        drawer.style.removeProperty('--child-drawer-width');
      };
    }
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, [childDrawerWidth]);

  // when a drawer becomes the upfront drawer (new child opens, or a child closes)
  // set ancestors --child-drawer-width to the upfront drawer's width
  useIsomorphicLayoutEffect(() => {
    const drawer = drawerRef.current;

    if (!isStackParent && drawer) {
      // eslint-disable-next-line @sail/ui-no-reflow
      parentContext?.setChildDrawerWidth(drawer.clientWidth);
    }
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, [isStackParent]);

  useResizeObserver({
    node: drawerRef,
    avoidInitialEvent: true,
    handler: React.useCallback(
      (entry) => {
        // eslint-disable-next-line @sail/ui-no-reflow
        parentContext?.setChildDrawerWidth(entry.target.clientWidth);
      },
      [parentContext],
    ),
  });

  return {
    isStackParent,
    isStackChild: Boolean(parentContext),
    drawerStackProvider: provider(DrawerStackContext, context),
  };
}

export const DrawerDesktop = createView<DrawerProps>(
  ({
    children,
    confirmCloseMessages,
    bottomSheetVariant,
    header,
    footer,
    onClose,
    disableDefaultBottomSheetHeader,
    direction = 'right',
    ...props
  }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const drawerRef = useRef<HTMLDivElement>(null);
    const {isStackChild, isStackParent, drawerStackProvider} =
      useDrawerStack(drawerRef);
    const directionSign = direction === 'left' ? -1 : 1;

    useEffect(() => {
      // eslint-disable-next-line @sail/ui-no-reflow
      drawerRef.current?.focus();
    }, [drawerRef]);

    const onConfirmClose = () => {
      if (confirmCloseMessages) {
        setShowConfirm(true);
      } else {
        onClose && onClose();
      }
    };

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onConfirmClose();
      }
    }

    const desktopTransformAnimations = (disabled: boolean) => [
      animate.in({
        duration: isStackChild
          ? ANIMATION_DURATION_MEDIUM
          : ANIMATION_DURATION_SHORT,
        transform: isStackChild
          ? `translateX(${100 * directionSign}%)`
          : `translateX(${24 * directionSign}%)`,
        easing: ANIMATION_EASING,
        disabled,
      }),
      animate.out({
        duration: ANIMATION_DURATION_LONG,
        transform: `translateX(${120 * directionSign}%)`,
        opacity: 1,
        easing: ANIMATION_EASING,
        disabled,
      }),
    ];

    return (
      <BaseDrawer
        // eslint-disable-next-line react/jsx-no-bind
        onKeyDown={onKeyDown}
        {...props}
        direction={direction}
        ref={drawerRef}
        onClose={onConfirmClose}
        uses={[
          provider(DrawerContext, {
            onClose: onConfirmClose,
            registerLabelId: () => {
              // This should be stored in the future in a useState and used to set aria-labelledby.
            },
            registerDescriptionId: () => {
              // This should be stored in the future in a useState and used to set aria-describedby.
            },
          }),
          drawerStackProvider,
          animate.in({
            duration: isStackChild ? 0 : ANIMATION_DURATION_SHORT,
            opacity: 0.8,
            easing: ANIMATION_EASING,
          }),
          animate
            .transition({
              duration: ANIMATION_DURATION_SHORT,
              transform: `
                translateX(calc((100% - var(--child-drawer-width, 0) - ${$['space.200']}) * ${directionSign}))
                perspective(57.2mm)
                translateZ(calc(${$['space.150']} / -2))`,
            })
            .when(isStackParent),
          ...desktopTransformAnimations(false),
        ]}
        header={header}
      >
        <view.div
          css={{
            position: 'relative',
            overflowY: 'auto',
            overscrollBehaviorY: 'contain',
            flexShrink: 1,
          }}
        >
          {children}
        </view.div>
        <view.div css={{height: 'fit'}}>{footer}</view.div>
        <AnimatePresence>
          {confirmCloseMessages && showConfirm ? (
            <CloseConfirmationDialog
              messages={confirmCloseMessages}
              onClose={onClose}
              onCancel={() => setShowConfirm(false)}
            />
          ) : null}
        </AnimatePresence>
      </BaseDrawer>
    );
  },
);
